/**
 * The partner offer sheet — its decisions, pure (partner codes build item 5 / C5, 2026-09-27).
 *
 * Design §4 step 4 (docs/plans/PARTNER_CODES_PLATFORM_DESIGN.md): at the purchase moment a
 * person holding a partner discount sees the partner offer sheet instead of the paywall; a
 * person with none sees the paywall exactly as before. Everything the sheet DECIDES is here,
 * with no React Native import, so jest pins it (utils/__tests__/partnerOfferSheet.test.ts):
 *
 *   - `shouldCheckPartnerOffer`: whether to ask the server at all. Only on a phone, only for
 *     a grant with a discount level whose purchase is live (`partner_offer` on the status
 *     response, with `discount_purchase_live` — null for everyone while the program is dark,
 *     false while the purchase kill switch is off), never for a cohort that must not see a
 *     purchase screen, is already subscribed or on a store trial, or is unknown. Anything else
 *     → the paywall, with no request.
 *   - `partnerGateStatus`: which surface the paywall's open shows — `none` (the paywall),
 *     `checking` (a neutral spinner: this paywall's own status has not loaded yet, or the
 *     server has not answered — at most PARTNER_GATE_TIMEOUT_MS) or `show` (the sheet). The
 *     decision is taken once the status is known (review of #4115, H1: every native paywall
 *     mounts fresh, so its first render never has the status) and latched for the open.
 *   - `resolvePartnerRows`: the plans the sheet can actually sell, joined to RevenueCat's
 *     packages: a plan whose product is not in the offering is dropped; on Android a plan
 *     whose Play offer option is not among the package's subscription options is dropped
 *     and reported (the app falls back to the paywall and the server logs
 *     PARTNER_OFFER_FALLBACK).
 *   - `purchasePathFor`: iPhone → an App Store one-time code (the server allocates it, the
 *     app opens Apple's redemption link); Android → buy the Play offer's option.
 *   - `planTermsLine`: the price line (see constants/partnerOfferSheetCopy.ts for why the
 *     iPhone line states a percentage).
 *   - `allocationRefusalAction`: what the app does with a refused allocation.
 *   - `rowsForHeldPlan`: after `code_held_for_period`, only the plan the code is held for in
 *     its period; the other period's plans stay.
 *   - `clearsPartnerAttributesOnHandover`: whether a handover to the full-price paywall clears
 *     the partner attributes (not when a store subscription or trial is already running).
 *   - `startForegroundWatchdog`: the bound on how long a purchase can keep the sheet busy
 *     (`isForegroundAppState`: what counts as the foreground for it).
 */
import { PARTNER_OFFER_SHEET } from '../constants/partnerOfferSheetCopy';

export type PartnerPeriod = 'monthly' | 'annual';
export type PartnerStore = 'app_store' | 'google_play';

/** One plan the server says this person can buy with their discount. */
export interface PartnerOfferPlan {
  product_id: string;
  period: PartnerPeriod;
  tier: 'basic' | 'full' | 'extended';
  level_pct: number;
  /** RevenueCat's product identifier (Android: `{productId}:{basePlanId}`). */
  rc_product_id: string;
  /** Android: the Play offer's option, `{basePlanId}:{offerId}`. iPhone: null. */
  option_id: string | null;
  /** Set on the RevenueCat customer before the purchase (never personal data). */
  /** `cedar_offer_source` ('house' | 'partner') comes from servers with Codes PR-4+5; older servers omit it. */
  rc_attributes: { cedar_partner: string; cedar_partner_offer: string; cedar_offer_source?: string };
  /** iPhone: this person already holds an App Store code for this plan. */
  held?: boolean;
  /** How many months the discount lasts (Codes PR-2): a monthly plan's own term (1, 3, 6 or
   *  12), an annual plan's 12 (the first year). Optional: absent from a server before PR-2 —
   *  the app then reads 12, exactly as before. */
  months?: number | null;
  /** The server's plain line, "15% off for your first 3 months" (no price) — a fallback for
   *  other clients; the sheet builds its priced line from `months` (constants/partnerOfferSheetCopy). */
  terms_line?: string | null;
}

/** GET /api/user/partner-offer (routers/partner_offer_purchase.py). */
export interface PartnerPurchaseOffer {
  eligible: boolean;
  reason: string | null;
  store?: PartnerStore;
  partner_name?: string | null;
  levels?: { monthly: number | null; annual: number | null };
  /** How long each discount lasts, in months (Codes PR-2; optional — see PartnerOfferPlan.months). */
  terms?: { monthly: number | null; annual: number | null };
  plans?: PartnerOfferPlan[];
  /** A refusal's own words (only `offer_code_limit` carries them — the owner's wording). */
  message?: string | null;
}

/** The server's refusal that comes with words for the person: the most App Store offer codes a
 *  period allows have been handed out (services/partner_discount_purchase.OFFER_CODE_LIMIT). */
export const OFFER_CODE_LIMIT = 'offer_code_limit';

/** The longest refusal text the sheet will show; anything longer is not the server's words. */
const MAX_REFUSAL_WORDS = 400;

/** The words to show for a refusal, or null. Only `offer_code_limit` has words: the server's
 *  own (trimmed, bounded) when it sent them, else the app's copy of the same text. Any other
 *  reason — or none — is null: those hand over to the paywall exactly as before. Pure. */
export function serverRefusalWords(reason: string | null | undefined, message?: unknown): string | null {
  if (reason !== OFFER_CODE_LIMIT) return null;
  if (typeof message === 'string') {
    const words = message.trim();
    if (words.length > 0 && words.length <= MAX_REFUSAL_WORDS) return words;
  }
  return PARTNER_OFFER_SHEET.offerCodeLimit;
}

/** The part of `partner_offer` (GET /api/user/subscription-status) the gate reads. */
export interface PartnerOfferSummary {
  discount_monthly_pct: number | null;
  discount_annual_pct: number | null;
  /** Buying with the discount is live (FF_PARTNER_ATTRIBUTION and FF_PARTNER_DISCOUNT_PURCHASE). */
  discount_purchase_live?: boolean;
  /** Codes PR-2 (optional): the monthly discount's term in months; the annual one's (12). */
  discount_monthly_months?: number | null;
  discount_annual_months?: number | null;
  /** Codes PR-2: the server withheld a monthly discount this app cannot word (then its pct is null). */
  discount_monthly_withheld?: boolean;
}

/** A monthly discount's term the app can word: a whole number of months, 1–12 (the stores'
 *  range, services/discount_offers.py). Anything else — absent, an older server, a bad value —
 *  is null, and the copy reads 12, exactly as before Codes PR-2. Pure. */
export function discountTermMonths(months: unknown): number | null {
  return typeof months === 'number' && Number.isInteger(months) && months >= 1 && months <= 12 ? months : null;
}

// ── RevenueCat shapes, structurally (no react-native-purchases import here) ──
export interface RcPrice { formatted: string; amountMicros?: number }
export interface RcPeriod { unit?: string; value?: number; iso8601?: string }
export interface RcPricingPhase {
  billingPeriod?: RcPeriod | null;
  billingCycleCount?: number | null;
  price: RcPrice;
}
export interface RcSubscriptionOption {
  id: string;
  introPhase?: RcPricingPhase | null;
  fullPricePhase?: RcPricingPhase | null;
  pricingPhases?: RcPricingPhase[] | null;
}
export interface RcCustomerInfoLike {
  activeSubscriptions?: string[] | null;
  allPurchaseDates?: Record<string, string | null> | null;
  entitlements?: { active?: Record<string, unknown> | null } | null;
}
export interface RcPackageLike {
  product: {
    identifier: string;
    priceString: string;
    subscriptionOptions?: RcSubscriptionOption[] | null;
  };
}

/** Cohorts the sheet never shows to: no purchase screen at all (decisions 14, 26), a
 *  store-backed subscription already running (Apple's offer codes are for new and lapsed
 *  subscribers only), or a cohort nobody could read (`unknown`). Mirrors
 *  services/revenueCatPaywall's two lists; a `trial_active` person on a STORE trial (not the
 *  Cedar clock) is excluded too — see shouldCheckPartnerOffer. The server refuses the same
 *  people (services/partner_discount_purchase.store_backed_refusal). */
export const PARTNER_SHEET_EXCLUDED_COHORTS: readonly string[] = [
  'free_month_active', 'grant', 'paid_active', 'paid_cancelled_grace', 'billing_issue', 'unknown',
];

/** How long the paywall waits for the server before showing the paywall instead. */
export const PARTNER_GATE_TIMEOUT_MS = 4000;

export function isPhone(platform: string): boolean {
  return platform === 'ios' || platform === 'android';
}

export function shouldCheckPartnerOffer(input: {
  platform: string;
  partnerOffer: PartnerOfferSummary | null | undefined;
  cedarCohort: string | null | undefined;
  /** `time_bound_trial` on the status response: the Seven Free Days model is on for this
   *  person. It does not prove THIS trial is a clock week — a `trial_active` person without it
   *  gets the paywall with no request; with it the server is asked, and the server's own
   *  check (the Cedar-clock predicate, store_backed_refusal) refuses a store trial. */
  timeBoundTrial?: boolean;
}): boolean {
  const { platform, partnerOffer, cedarCohort, timeBoundTrial } = input;
  if (!isPhone(platform)) return false;
  if (!partnerOffer) return false;
  if (partnerOffer.discount_purchase_live !== true) return false;
  if (partnerOffer.discount_monthly_pct == null && partnerOffer.discount_annual_pct == null) return false;
  if (!cedarCohort || PARTNER_SHEET_EXCLUDED_COHORTS.includes(cedarCohort)) return false;
  if (cedarCohort === 'trial_active' && !timeBoundTrial) return false;
  return true;
}

export type PartnerGateStatus = 'none' | 'checking' | 'show';
export interface PartnerGateAnswer { status: 'none' | 'show'; offer: PartnerPurchaseOffer | null }

/**
 * Which surface this open shows. `decided` is the latched decision for the open: null while
 * this paywall's status is still loading (→ `checking`, a neutral spinner — never a blank
 * screen and never the wrong paywall), false for "not a discount holder" (→ `none`), true
 * while the server is asked (→ `checking`, then its answer). A status that could not be
 * read at all decides `none` at once (the caller passes decided=false).
 */
export function partnerGateStatus(input: {
  phone: boolean;
  visible: boolean;
  decided: boolean | null;
  answer: PartnerGateAnswer | null;
}): PartnerGateStatus {
  if (!input.phone || !input.visible) return 'none';
  if (input.answer) return input.answer.status;
  if (input.decided === false) return 'none';
  return 'checking';
}

/** The server's answer as the gate's decision: the sheet for an eligible offer with at least
 *  one plan for this store — and for `offer_code_limit`, so the person reads WHY their discount
 *  is gone (the sheet shows the words with "See all plans" and "Not now", no plans) instead of
 *  meeting full prices with no explanation. Every other refusal is the paywall. */
export function gateAnswerFor(offer: PartnerPurchaseOffer | null | undefined): PartnerGateAnswer {
  if (offer && offer.eligible && Array.isArray(offer.plans) && offer.plans.length > 0) {
    return { status: 'show', offer };
  }
  if (offer && !offer.eligible && serverRefusalWords(offer.reason, offer.message) !== null) {
    return { status: 'show', offer };
  }
  return { status: 'none', offer: null };
}

export interface PartnerPlanRow {
  plan: PartnerOfferPlan;
  /** The store's regular price for the product (RevenueCat's localized string). */
  regularPrice: string;
  /** Android: Google Play's discounted price for the offer. iPhone: null (see the copy file). */
  discountedPrice: string | null;
  /** Android: how many periods the discounted price lasts (the offer's own count). */
  discountCycles: number | null;
  /** Android: the option to buy. iPhone: null. */
  option: RcSubscriptionOption | null;
}

export interface PartnerRowsResult {
  rows: PartnerPlanRow[];
  /** Plans dropped because their Play offer option is not in the offering. */
  missingOptions: PartnerOfferPlan[];
  /** Plans dropped because their product is not in the offering. */
  missingProducts: PartnerOfferPlan[];
}

/** The discounted phase of a Play offer: RevenueCat's `introPhase`, else the first phase that
 *  is not the full price. */
function discountPhase(option: RcSubscriptionOption): RcPricingPhase | null {
  if (option.introPhase?.price?.formatted) return option.introPhase;
  const full = option.fullPricePhase;
  for (const ph of option.pricingPhases ?? []) {
    if (ph !== full && ph?.price?.formatted && ph.billingCycleCount != null) return ph;
  }
  return null;
}

export function resolvePartnerRows(
  plans: PartnerOfferPlan[] | null | undefined,
  packages: RcPackageLike[] | null | undefined,
  platform: string,
): PartnerRowsResult {
  const byProduct = new Map<string, RcPackageLike>();
  for (const pkg of packages ?? []) {
    const id = pkg?.product?.identifier;
    if (id && !byProduct.has(id)) byProduct.set(id, pkg);
  }
  const out: PartnerRowsResult = { rows: [], missingOptions: [], missingProducts: [] };
  for (const plan of plans ?? []) {
    const pkg = byProduct.get(plan.rc_product_id);
    if (!pkg || !pkg.product.priceString) {
      out.missingProducts.push(plan);
      continue;
    }
    if (platform === 'android') {
      const option = (pkg.product.subscriptionOptions ?? []).find((o) => o?.id === plan.option_id) ?? null;
      if (!option || !plan.option_id) {
        out.missingOptions.push(plan);
        continue;
      }
      const phase = discountPhase(option);
      out.rows.push({
        plan,
        regularPrice: option.fullPricePhase?.price?.formatted || pkg.product.priceString,
        discountedPrice: phase?.price?.formatted ?? null,
        discountCycles: phase?.billingCycleCount ?? null,
        option,
      });
    } else {
      out.rows.push({ plan, regularPrice: pkg.product.priceString, discountedPrice: null, discountCycles: null, option: null });
    }
  }
  // Monthly first, then annual; within a period the server's order (Essentials, Chat+, Pro).
  out.rows.sort((a, b) => (a.plan.period === b.plan.period ? 0 : a.plan.period === 'monthly' ? -1 : 1));
  return out;
}

/** The price line for one plan. A monthly discount's length is its TERM (Codes PR-2): the
 *  server's `months` for the plan ("for your first 3 months", "for your first month"); without
 *  one (a server before PR-2) the first 12 months, exactly as before. An annual discount is
 *  always the first year. Android prefers the Play offer's own count (what Google charges —
 *  each term is its own Play offer, so they agree), and falls back to the term. */
export function planTermsLine(row: PartnerPlanRow, platform: string): string {
  const c = PARTNER_OFFER_SHEET;
  const { plan } = row;
  const term = plan.period === 'monthly' ? discountTermMonths(plan.months) : null;
  if (platform === 'android' && row.discountedPrice) {
    const n = row.discountCycles && row.discountCycles > 0
      ? row.discountCycles
      : plan.period === 'monthly' ? (term ?? 12) : 1;
    return plan.period === 'monthly'
      ? c.playMonthlyTerms(row.discountedPrice, n, row.regularPrice)
      : c.playAnnualTerms(row.discountedPrice, n, row.regularPrice);
  }
  return plan.period === 'monthly'
    ? c.appleMonthlyTerms(plan.level_pct, row.regularPrice, term)
    : c.appleAnnualTerms(plan.level_pct, row.regularPrice);
}

export type PartnerPurchasePath = 'apple_code' | 'play_option';

export function purchasePathFor(platform: string): PartnerPurchasePath {
  return platform === 'android' ? 'play_option' : 'apple_code';
}

/** Allocation refusals that mean "this person has no discount to buy with any more" (the
 *  server's reasons, services/partner_discount_purchase.py): the paywall takes over.
 *  `code_held_for_period` is NOT one — the discount is still theirs, held for one plan (`held`).
 *  Every other refusal (out of stock, the network) keeps the sheet and says so. */
export const ALLOCATION_REFUSALS_TO_PAYWALL: readonly string[] = [
  'disabled', 'no_grant', 'no_discount', 'tag_gone', 'partner_ended', 'partner_inactive',
  'tag_ended', 'tag_inactive', 'no_discount_for_period', 'offer_inactive', 'bad_product',
  'store_subscription_active', 'store_trial_active',
  // Codes PR-2: the server will not sell a term this app has not said it can word (it sends
  // X-Client-Caps: partner_offer_terms, so this should never come) — the normal paywall.
  'term_needs_app_update',
];

export type AllocationRefusalAction = 'paywall' | 'held' | 'out_of_stock' | 'limit' | 'retry';

export function allocationRefusalAction(code: string | null | undefined): AllocationRefusalAction {
  if (code && ALLOCATION_REFUSALS_TO_PAYWALL.includes(code)) return 'paywall';
  // The person has had the most codes this period allows: say so with the server's words —
  // retrying can never succeed, so it is not 'retry' ("Something went wrong. Please try again.").
  if (code === OFFER_CODE_LIMIT) return 'limit';
  if (code === 'code_held_for_period') return 'held';
  if (code === 'out_of_stock') return 'out_of_stock';
  return 'retry';
}

/** After `code_held_for_period`: in the held plan's billing period the sheet offers only the plan
 *  the person's code is held for (review of #4115, M-A — they have used their switches for that
 *  period); the OTHER period's plans stay, discounted as before — its switches are its own, and
 *  the server's own read (narrow_to_held) narrows only a locked period (review of #4115, round 3,
 *  LOW-2). The
 *  held product unknown or not among the rows → no rows (the caller shows the paywall). Pure. */
export function rowsForHeldPlan(rows: PartnerPlanRow[], heldProductId: string | null | undefined): PartnerPlanRow[] {
  if (!heldProductId) return [];
  const held = rows.find((r) => r.plan.product_id === heldProductId);
  if (!held) return [];
  return rows.filter((r) => r.plan.period !== held.plan.period || r.plan.product_id === heldProductId);
}

/** Why the sheet hands over to the full-price paywall, when the reason is NOT the person
 *  switching to full price: the store already has a subscription (or a store trial) running for
 *  them — in this sheet, most likely THIS discounted purchase, landed late (Apple's redemption
 *  after the person came back; a Play payment that cleared). Clearing the partner attributes then
 *  would strip the partner tie from the very purchase it belongs to (review of #4115, round 3,
 *  LOW-1). The server's reasons (services/partner_discount_purchase.py). */
export const HANDOVER_REASONS_KEEPING_PARTNER_ATTRIBUTES: readonly string[] = [
  'store_subscription_active', 'store_trial_active',
];

/** Does handing over to the full-price paywall for `reason` clear the partner attributes?
 *  Yes — the person is leaving the discount for full price ("See all plans", the discount gone)
 *  — except for a store subscription or trial already running (above). No reason = a switch. */
export function clearsPartnerAttributesOnHandover(reason: string | null | undefined): boolean {
  return !(reason != null && HANDOVER_REASONS_KEEPING_PARTNER_ATTRIBUTES.includes(reason));
}

/** The longest a purchase may keep the sheet busy — every way out disabled, Android's Back
 *  swallowed — while the app is in the FOREGROUND (review of #4115, round 3, LOW-4). Time spent
 *  in the App Store or on Google Play's sheet does not count: a person may take their time there.
 *  Past it the sheet gives up on the attempt (the generic error) so it can never trap anyone. A
 *  purchase that still lands afterwards is honoured — even once the sheet has closed (review of
 *  #4201, LOW-C5-a: the host's mount-independent onPurchased, at most once). */
export const PARTNER_PURCHASE_WATCHDOG_MS = 90_000;

/** How long one real purchase is reported once (review of the FE single shot, L-1): the same
 *  user + product reported again within this window is the same purchase, seen by a second
 *  attempt or a second sheet (a given-up attempt on one sheet landing after another sheet's). */
export const PARTNER_PURCHASE_REPORT_WINDOW_MS = 30 * 60_000;

/** Module-scoped, so it outlives any one sheet: user + product → when it was last reported. */
const partnerPurchaseReports = new Map<string, number>();

function purchaseReportKey(userId: string | null | undefined, productId: string): string {
  return `${userId || 'anon'}|${productId}`;
}

/**
 * The once-per-purchase latch for the host's onPurchased (onPurchaseSuccess — the trial-gate
 * resume — and the backup subscription-activate). True: report it (and it is now recorded).
 * False: this user + product was reported within PARTNER_PURCHASE_REPORT_WINDOW_MS — the same
 * purchase again, from any sheet. Entries past the window are dropped as it goes.
 */
export function claimPartnerPurchaseReport(
  userId: string | null | undefined,
  productId: string,
  now: number = Date.now(),
): boolean {
  for (const [k, at] of partnerPurchaseReports) {
    if (now - at >= PARTNER_PURCHASE_REPORT_WINDOW_MS) partnerPurchaseReports.delete(k);
  }
  const key = purchaseReportKey(userId, productId);
  if (partnerPurchaseReports.has(key)) return false;
  partnerPurchaseReports.set(key, now);
  return true;
}

/** Was any partner purchase reported for this user within the window (any sheet, any
 *  product)? A decline held back by one sheet is then never recorded (L-1). */
export function partnerPurchaseReportedRecently(userId: string | null | undefined, now: number = Date.now()): boolean {
  const prefix = `${userId || 'anon'}|`;
  for (const [k, at] of partnerPurchaseReports) {
    if (k.startsWith(prefix) && now - at < PARTNER_PURCHASE_REPORT_WINDOW_MS) return true;
  }
  return false;
}

/** Tests only: forget every reported purchase. */
export function resetPartnerPurchaseReportsForTests(): void {
  partnerPurchaseReports.clear();
}

/** The part of React Native's AppState the watchdog reads (passed in: this file imports no RN). */
export interface AppStateLike {
  addEventListener(type: 'change', listener: (state: string) => void): { remove: () => void };
}

/** Does the watchdog count `state` as the foreground? 'active', and also a state React Native
 *  cannot name yet — null / undefined, or 'unknown' (iOS reports it before the first change;
 *  review of #4201, NIT-C5-b 2): a state nobody knows must never pause the bound, or the sheet
 *  could stay busy for good. 'background' / 'inactive' (Apple's sheet, Google Play's) and
 *  'extension' are away. */
export function isForegroundAppState(state: string | null | undefined): boolean {
  return state == null || state === 'active' || state === 'unknown';
}

/**
 * Calls `onExpire` once the app has spent `ms` in the foreground in ONE stretch without the
 * returned stop function being called. Leaving the foreground ('background' / 'inactive' —
 * Apple's sheet, Google Play's) pauses it; coming back starts a fresh stretch, so a timer
 * overdue from the background never fires the moment the person returns. A foreground change
 * while a stretch is already running leaves it running. What counts as the foreground is
 * `isForegroundAppState`: 'active', and a state not known yet (null / undefined / 'unknown'),
 * both at the start (`currentState`) and on a change. Fires at most once; stop is idempotent.
 */
export function startForegroundWatchdog(
  ms: number,
  onExpire: () => void,
  appState: AppStateLike,
  currentState: string | null | undefined,
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let done = false;
  let sub: { remove: () => void } | null = null;
  const disarm = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const stop = () => {
    if (done) return;
    done = true;
    disarm();
    try { sub?.remove(); } catch { /* already gone */ }
  };
  const arm = () => {
    disarm();
    timer = setTimeout(() => {
      timer = null;
      if (done) return;
      stop();
      onExpire();
    }, ms);
  };
  sub = appState.addEventListener('change', (state: string) => {
    if (done) return;
    if (!isForegroundAppState(state)) disarm();
    else if (timer === null) arm();          // back in the foreground: a fresh stretch
  });
  if (isForegroundAppState(currentState)) arm();
  return stop;
}

/**
 * After Apple's sheet: was THIS product just bought? True when the product is now an active
 * subscription and either was not before the sheet opened, or its purchase date is later than
 * the one read before (review of #4115, L1 — any active entitlement is not proof: it could be
 * another plan). `before` null (the first read failed) → only "active now" counts.
 */
export function purchasedAfterStore(
  productId: string,
  before: RcCustomerInfoLike | null | undefined,
  after: RcCustomerInfoLike | null | undefined,
): boolean {
  const activeNow = (after?.activeSubscriptions ?? []).includes(productId);
  if (!activeNow) return false;
  if (!before) return true;
  const wasActive = (before.activeSubscriptions ?? []).includes(productId);
  if (!wasActive) return true;
  const was = Date.parse(before.allPurchaseDates?.[productId] ?? '') || 0;
  const now = Date.parse(after?.allPurchaseDates?.[productId] ?? '') || 0;
  return now > was;
}

/** RevenueCat's PAYMENT_PENDING_ERROR code (react-native-purchases'
 *  PURCHASES_ERROR_CODE.PAYMENT_PENDING_ERROR, "20"): Google Play accepted the purchase but the
 *  payment has not cleared (a cash or bank method). Not a failure — it may still land. */
export const RC_PAYMENT_PENDING_CODE = '20';

/** Is this RevenueCat purchase error a pending payment? Pure. */
export function isPaymentPendingError(e: unknown): boolean {
  const err = e as { code?: unknown; userInfo?: { readableErrorCode?: unknown } } | null | undefined;
  if (!err) return false;
  return String(err.code) === RC_PAYMENT_PENDING_CODE || err.userInfo?.readableErrorCode === 'PAYMENT_PENDING_ERROR';
}

/** The tier a RevenueCat customer's active entitlements unlock, or null (nothing active). */
export function activeTierFromEntitlements(active: Record<string, unknown> | null | undefined):
  'basic' | 'full' | 'extended' | null {
  if (!active) return null;
  if (active.extended_plan) return 'extended';
  if (active.full_plan) return 'full';
  if (active.basic_plan) return 'basic';
  return null;
}
